import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  Users, TrendingUp, CheckCircle, BookOpen, FileText, 
  LogOut, Clock, Award, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [studentAttendance, setStudentAttendance] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/parent/login');
    } else if (user && user.role !== 'parent') {
      navigate('/');
    } else if (user) {
      fetchDashboard();
    }
  }, [user, authLoading, navigate]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/parent/dashboard`, { withCredentials: true });
      setDashboard(res.data);
      
      // Fetch attendance for each student
      const attendanceData = {};
      for (const studentData of res.data.students || []) {
        try {
          const attRes = await axios.get(`${API}/parent/student/${studentData.student.id}/attendance`, { withCredentials: true });
          attendanceData[studentData.student.id] = attRes.data;
        } catch (e) {
          console.error('Failed to fetch attendance for student');
        }
      }
      setStudentAttendance(attendanceData);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/parent/login');
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Welcome, {user?.name?.split(' ')[0]}!</h1>
            <p className="text-[#94A3B8]">Track your child's learning progress</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="parent-logout-btn"
            className="flex items-center gap-2 bg-[#0A1F44] text-[#94A3B8] hover:text-white px-4 py-2 rounded-xl border border-[#00AEEF]/20 hover:border-[#FF6A00] transition-all"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {dashboard?.students?.length > 0 ? (
          <div className="space-y-8">
            {dashboard.students.map((studentData, idx) => (
              <div key={idx} className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
                {/* Student Header */}
                <div className="p-6 border-b border-[#00AEEF]/10">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00AEEF] to-[#00FFE0] flex items-center justify-center">
                      <span className="text-2xl font-bold text-[#060F22]">
                        {studentData.student?.name?.charAt(0) || 'S'}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">{studentData.student?.name}</h2>
                      <p className="text-[#94A3B8]">{studentData.student?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Progress Stats */}
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-[#060F22]/50 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="w-8 h-8 text-[#00AEEF]" />
                        <div>
                          <p className="text-2xl font-bold text-white">{studentData.stats?.completion_percentage || 0}%</p>
                          <p className="text-xs text-[#94A3B8]">Progress</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-[#060F22]/50 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-8 h-8 text-[#00FFE0]" />
                        <div>
                          <p className="text-2xl font-bold text-white">{studentData.stats?.completed_lessons || 0}</p>
                          <p className="text-xs text-[#94A3B8]">Lessons Done</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-[#060F22]/50 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <BookOpen className="w-8 h-8 text-[#FF6A00]" />
                        <div>
                          <p className="text-2xl font-bold text-white">{studentData.stats?.total_lessons || 0}</p>
                          <p className="text-xs text-[#94A3B8]">Total Lessons</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-[#060F22]/50 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-purple-400" />
                        <div>
                          <p className="text-2xl font-bold text-white">{studentData.stats?.assignments_submitted || 0}</p>
                          <p className="text-xs text-[#94A3B8]">Assignments</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-[#94A3B8]">Course Progress</span>
                      <span className="text-sm font-bold text-[#00FFE0]">{studentData.stats?.completion_percentage || 0}%</span>
                    </div>
                    <div className="w-full bg-[#060F22] rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-[#00AEEF] to-[#00FFE0] h-3 rounded-full transition-all duration-500"
                        style={{ width: `${studentData.stats?.completion_percentage || 0}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Attendance Section */}
                  {studentAttendance[studentData.student.id] && (
                    <div className="mb-6 p-4 bg-[#060F22]/50 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-[#00AEEF]" />
                          <span className="text-white font-medium">Attendance</span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          studentAttendance[studentData.student.id].stats.attendance_rate >= 80 ? 'bg-green-500/20 text-green-400' :
                          studentAttendance[studentData.student.id].stats.attendance_rate >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {studentAttendance[studentData.student.id].stats.attendance_rate}% Rate
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div>
                          <p className="text-lg font-bold text-white">{studentAttendance[studentData.student.id].stats.total_days}</p>
                          <p className="text-xs text-[#94A3B8]">Days</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-[#00FFE0]">{studentAttendance[studentData.student.id].stats.present}</p>
                          <p className="text-xs text-[#94A3B8]">Present</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-yellow-400">{studentAttendance[studentData.student.id].stats.late}</p>
                          <p className="text-xs text-[#94A3B8]">Late</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-red-400">{studentAttendance[studentData.student.id].stats.absent}</p>
                          <p className="text-xs text-[#94A3B8]">Absent</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recent Activity */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-4">Recent Activity</h3>
                    {studentData.progress?.length > 0 ? (
                      <div className="space-y-3">
                        {studentData.progress.slice(0, 5).map((p, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 bg-[#060F22]/50 rounded-xl">
                            <CheckCircle className="w-5 h-5 text-[#00FFE0]" />
                            <span className="text-[#94A3B8]">Completed a lesson</span>
                            <span className="text-[#94A3B8] text-sm ml-auto">
                              {new Date(p.completed_at).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[#94A3B8]">No recent activity</p>
                    )}
                  </div>

                  {/* Assignments */}
                  {studentData.submissions?.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-lg font-bold text-white mb-4">Recent Assignments</h3>
                      <div className="space-y-3">
                        {studentData.submissions.slice(0, 3).map((sub, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 bg-[#060F22]/50 rounded-xl">
                            <FileText className="w-5 h-5 text-[#FF6A00]" />
                            <span className="text-white">Assignment Submitted</span>
                            <span className={`ml-auto px-2 py-1 rounded-full text-xs ${
                              sub.status === 'submitted' ? 'bg-[#00AEEF]/20 text-[#00AEEF]' :
                              sub.status === 'graded' ? 'bg-[#00FFE0]/20 text-[#00FFE0]' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {sub.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-12 text-center">
            <Users className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">No Students Linked</h2>
            <p className="text-[#94A3B8]">Student accounts will appear here once enrollments are approved.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentDashboard;
